import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Phone, ArrowsClockwise, ArrowDown, ArrowUp, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function WhatsAppStats() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState({ inbound: [], outbound: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/whatsapp/stats"),
        api.get("/whatsapp/recent"),
      ]);
      setStats(s.data); setRecent(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !stats) return <div className="p-6 text-muted-foreground">Chargement…</div>;

  const cards = [
    { label: "Messages entrants (30j)", value: stats.inbound_30d, tone: "cyan", icon: <ArrowDown size={16}/> },
    { label: "Messages sortants (30j)", value: stats.outbound_30d, tone: "emerald", icon: <ArrowUp size={16}/> },
    { label: "Tickets créés via WhatsApp", value: stats.tickets_created_from_whatsapp, tone: "blue", icon: null },
    { label: "Taux de réponse (30j)", value: `${stats.response_rate_pct_30d}%`, tone: "amber", icon: null },
  ];
  const tone = {
    cyan:    "bg-cyan-50 border-cyan-200 text-cyan-800 dark:bg-cyan-950 dark:border-cyan-800 dark:text-cyan-300",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950 dark:border-emerald-800 dark:text-emerald-300",
    blue:    "bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-300",
    amber:   "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-300",
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="whatsapp-stats-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-caps">Module</div>
          <h1 className="font-display text-3xl font-bold mt-1 flex items-center gap-2">
            <Phone size={26} weight="duotone" className="text-primary"/> WhatsApp — Statistiques
          </h1>
          <div className="mt-2 text-sm">
            {stats.configured ? (
              <span className="inline-flex items-center gap-1.5 text-emerald-700"><CheckCircle size={14}/> Twilio connecté</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-amber-700"><XCircle size={14}/> Mode MOCK — ajoutez les clés Twilio pour envoyer en réel</span>
            )}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load}><ArrowsClockwise size={14} className="mr-1.5"/> Rafraîchir</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Card key={c.label} className={`border ${tone[c.tone]}`}>
            <CardContent className="py-4">
              <div className="text-[0.7rem] uppercase tracking-wider opacity-80">{c.label}</div>
              <div className="text-3xl font-bold mt-1">{c.value ?? 0}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="py-4"><div className="text-xs uppercase text-muted-foreground">Total sortants</div><div className="text-2xl font-bold mt-1">{stats.outbound_total}</div></CardContent></Card>
        <Card><CardContent className="py-4"><div className="text-xs uppercase text-muted-foreground">Total entrants</div><div className="text-2xl font-bold mt-1">{stats.inbound_total}</div></CardContent></Card>
        <Card><CardContent className="py-4"><div className="text-xs uppercase text-muted-foreground">Temps de réponse moyen (30j)</div><div className="text-2xl font-bold mt-1">{stats.avg_response_minutes_30d ? `${stats.avg_response_minutes_30d} min` : "—"}</div></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><ArrowDown size={16}/> Derniers messages entrants</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {recent.inbound.length === 0 ? <p className="text-sm text-muted-foreground">Aucun message reçu pour l'instant.</p> :
              recent.inbound.map((m) => (
                <div key={m.id} className="border rounded p-2 text-sm" data-testid={`wa-in-${m.id}`}>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{m.from}</span>
                    <span>→ Ticket #{m.ticket_number} {m.ticket_created && <span className="text-emerald-600">(nouveau)</span>}</span>
                  </div>
                  <div className="mt-1 line-clamp-2">{m.body || "(pièce jointe uniquement)"}</div>
                </div>
              ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><ArrowUp size={16}/> Derniers messages sortants</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {recent.outbound.length === 0 ? <p className="text-sm text-muted-foreground">Aucun message envoyé pour l'instant.</p> :
              recent.outbound.map((m) => (
                <div key={m.id} className="border rounded p-2 text-sm" data-testid={`wa-out-${m.id}`}>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Vers {m.to}</span>
                    <span>{m.mock ? <span className="text-amber-600">[MOCK]</span> : <span className="text-emerald-600">✓</span>} {m.kind || "manuel"}</span>
                  </div>
                  <div className="mt-1 line-clamp-2">{m.body}</div>
                </div>
              ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
